import React from 'react';
import { Link } from 'react-router-dom';

import Img from '../../../../components/Img';
import { getDefaultAvatar } from '../../../../utils/collegeCorner';
import { defaultTableFilters, SORT_ORDER } from '../helpers';
import AddToTargetListCell from './AddToTargetListCell';
import styles from './CppCollegeTeams.scss';

export const CPP_SORT_KEYS = {
    NAME: 'name',
    POWER6: 'power6',
    DIVISION: 'division',
    CONFERENCE: 'conference',
};

const normalizeGender = (gender) => {
    if (!gender) {
        return null;
    }
    const initial = `${gender}`.charAt(0).toUpperCase();
    return initial === 'M' || initial === 'F' ? initial : null;
};

export const getCppInitialFilters = (viewerGender = null) => ({
    ...defaultTableFilters,
    sort: CPP_SORT_KEYS.POWER6,
    order: SORT_ORDER.DESC,
    gender: normalizeGender(viewerGender),
    conference: null,
    division: null,
    locationInputValue: '',
    locationCountryName: '',
    locationStateName: '',
    distance: '50mi',
    location: '',
    lat: null,
    lng: null,
    utrFitRating: '',
    utrFitPosition: '',
});

const formatPower6 = (value) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return '';
    }
    return Number(value).toFixed(2);
};

const buildDisplayName = (item) => {
    const name = item?.name || '';
    const gender = normalizeGender(item?.gender);
    return gender ? `${name} - ${gender}` : name;
};

const getRowKey = (item, rowIndex) => item?.clubId ?? rowIndex;

const COLLEGE_DEFAULT_AVATAR = getDefaultAvatar({ isCollege: true });

const renderSortableHeader = (label) => (isActive) => (
    <span className={styles.headerTitle}>
        <span className={isActive ? styles.headerLabelActive : styles.headerLabel}>{label}</span>
        {!isActive && (
            <span
                className={styles.headerCaret}
                data-testid={`college-corner.college-teams.sort-${label?.toLowerCase()}-caret`}
            >
                {'▼'}
            </span>
        )}
    </span>
);

const renderHeader = (label) => () => (
    <span className={styles.headerTitle}>{label}</span>
);

export const getCppColumns = ({ viewerGender = null, clubRedirectBaseUrl = null } = {}) => ([
    {
        title: renderSortableHeader('Name'),
        sortLabel: 'Name',
        sortable: true,
        key: CPP_SORT_KEYS.NAME,
        maxWidth: 28,
        headerClassName: `${styles.nameHead}`,
        rowClassName: `${styles.nameCol}`,
        custom: (val, item, { rowIndex } = {}) => {
            const rowKey = getRowKey(item, rowIndex);
            const linkTestId = `college-corner.college-teams.team-${rowKey}.profile-link`;
            // When the Next.js redirect is enabled, link straight to the Next.js college page.
            // Routing through the in-app /colleges/:id route just to redirect leaves that entry in
            // history, so Back lands on it and bounces forward — forcing a second Back press.
            const nameLink = clubRedirectBaseUrl ? (
                <a
                    className="link"
                    href={`${clubRedirectBaseUrl}/${item.clubId}`}
                    data-testid={linkTestId}
                >
                    {buildDisplayName(item)}
                </a>
            ) : (
                <Link
                    className="link"
                    to={`/colleges/${item.clubId}`}
                    data-testid={linkTestId}
                >
                    {buildDisplayName(item)}
                </Link>
            );
            return (
                <div className={`${styles.collegeName}`}>
                    <span className={`${styles.displayLogo}`}>
                        <Img
                            src={item?.profilePhotoUrl || COLLEGE_DEFAULT_AVATAR}
                            defaultimg={COLLEGE_DEFAULT_AVATAR}
                            alt=""
                            className={`${styles.profilePicture}`}
                        />
                    </span>
                    <span className={`${styles.nameBlock}`}>
                        {nameLink}
                        <span
                            className={`${styles.mobileP6}`}
                            data-testid={`college-corner.college-teams.team-${rowKey}.mobile-p6`}
                        >
                            {`P6: ${formatPower6(item?.power6)}`}
                        </span>
                    </span>
                </div>
            );
        },
    },
    {
        title: renderSortableHeader('P6'),
        sortLabel: 'P6',
        sortable: true,
        key: CPP_SORT_KEYS.POWER6,
        maxWidth: 8,
        headerClassName: `${styles.psixHead}`,
        rowClassName: `${styles.psixCol}`,
        custom: (val, item) => formatPower6(item?.power6),
    },
    {
        title: renderSortableHeader('Division'),
        sortLabel: 'Division',
        sortable: true,
        key: CPP_SORT_KEYS.DIVISION,
        maxWidth: 16,
        headerClassName: `${styles.divisionHead}`,
        rowClassName: `${styles.divisionCol}`,
        custom: (val, item) => item?.conference?.division?.name || '',
    },
    {
        title: renderSortableHeader('Conference'),
        sortLabel: 'Conference',
        sortable: true,
        key: CPP_SORT_KEYS.CONFERENCE,
        maxWidth: 18,
        headerClassName: `${styles.conferenceHead}`,
        rowClassName: `${styles.conferenceCol}`,
        custom: (val, item) => item?.conference?.name || '',
    },
    {
        title: renderHeader('Location'),
        sortable: false,
        key: 'location',
        maxWidth: 16,
        headerClassName: `${styles.locationHead}`,
        rowClassName: `${styles.locationCol}`,
        custom: (val, item) => item?.location?.display || '',
    },
    {
        title: renderHeader('Add to List'),
        sortable: false,
        key: 'addToList',
        maxWidth: 12,
        headerClassName: `${styles.addHead}`,
        rowClassName: `${styles.addCol}`,
        custom: (val, item, { rowIndex } = {}) => (
            <AddToTargetListCell
                clubId={item.clubId}
                collegeName={buildDisplayName(item)}
                isTargetCollege={!!item.isTargetCollege}
                rowGender={normalizeGender(item?.gender)}
                viewerGender={normalizeGender(viewerGender)}
                data-testid={`college-corner.college-teams.add-${getRowKey(item, rowIndex)}`}
            />
        ),
    },
]);
